/**
 * Haptics utility — wraps @capacitor/haptics with silent web fallback.
 * Safe to call on web (no-ops) and in native iOS.
 *
 * User preference stored in localStorage: 'evcs_haptics_enabled' (default: true).
 *
 * Best-practice taxonomy used in this app:
 *   light   — UI taps, confirmations, subtle milestones
 *   medium  — level start, note advance, moderate events
 *   heavy   — (reserved for future destructive actions)
 *   success — level complete (good), calibration saved, achievement
 *   warning — level failed with poor score
 *   error   — hard failure / blocking error
 *
 * Complex patterns (achievement double-pulse) are composed here so call-sites
 * stay expressive without knowing implementation details.
 */
import { Capacitor } from '@capacitor/core';

let Haptics = null;

// Lazy-load only in native context to keep web bundle lean.
if (Capacitor.isNativePlatform()) {
  import('@capacitor/haptics').then((mod) => {
    Haptics = mod.Haptics;
  });
}

// ── User preference ───────────────────────────────────────────────────────────

const PREF_KEY = 'evcs_haptics_enabled';

/**
 * Returns false only when the user has explicitly disabled haptics.
 * Defaults to true on first run.
 */
export const isHapticsEnabled = () => {
  try {
    const v = localStorage.getItem(PREF_KEY);
    return v === null ? true : v === 'true';
  } catch (_) {
    return true;
  }
};

/**
 * Persist the user's haptics preference and notify listeners.
 */
export const setHapticsEnabled = (enabled) => {
  try {
    localStorage.setItem(PREF_KEY, enabled ? 'true' : 'false');
    window.dispatchEvent(
      new CustomEvent('hapticsPreferenceChanged', { detail: { enabled } }),
    );
  } catch (_) {}
};

// ── Core trigger ──────────────────────────────────────────────────────────────

/**
 * Fire a single haptic pattern.
 * Silently ignored on web or when the user has disabled haptics.
 * @param {'success'|'warning'|'error'|'medium'|'light'|'heavy'} type
 */
export const triggerHaptic = async (type = 'medium') => {
  if (!Haptics || !isHapticsEnabled()) return;
  try {
    if (type === 'success' || type === 'warning' || type === 'error') {
      const { HapticsNotificationType } = await import('@capacitor/haptics');
      const map = {
        success: HapticsNotificationType.SUCCESS,
        warning: HapticsNotificationType.WARNING,
        error:   HapticsNotificationType.ERROR,
      };
      await Haptics.notification({ type: map[type] });
    } else {
      const { ImpactStyle } = await import('@capacitor/haptics');
      const style =
        type === 'light'  ? ImpactStyle.Light
        : type === 'heavy'  ? ImpactStyle.Heavy
        : ImpactStyle.Medium;
      await Haptics.impact({ style });
    }
  } catch (_) {
    // Never crash on haptic failure
  }
};

// ── Semantic helpers ──────────────────────────────────────────────────────────
// Named wrappers so call-sites have clear intent without knowing impact types.

/** Light tap — confirms a UI button press. */
export const hapticTap = () => triggerHaptic('light');

/** Opening the side-drawer. */
export const hapticDrawerOpen = () => triggerHaptic('medium');

/** The singing window opens (grace period expired or first pitch detected). */
export const hapticGraceExpiry = () => triggerHaptic('light');

/** Advance to the next note during a level. */
export const hapticNoteAdvance = () => triggerHaptic('light');

/** Pitch first locks into the green zone. */
export const hapticGreenZoneEntry = () => triggerHaptic('light');

/**
 * Level complete — intensity scales with star rating.
 *   5 stars → rich success notification
 *   3–4     → medium impact
 *   < 3     → light (still positive, don't punish)
 */
export const hapticLevelComplete = (stars) => {
  if (stars >= 5) return triggerHaptic('success');
  if (stars >= 3) return triggerHaptic('medium');
  return triggerHaptic('light');
};

/**
 * Achievement unlocked — a success pulse followed 350 ms later by a light
 * confirming tap, creating a distinctive "double-knock" signature.
 */
export const hapticAchievement = async () => {
  await triggerHaptic('success');
  setTimeout(() => triggerHaptic('light'), 350);
};

/** Voice calibration completed successfully. */
export const hapticCalibrationSaved = () => triggerHaptic('success');
